import { observer } from "mobx-react-lite"
import { useStore } from "../../stores/root-store-context";
import {
    Button, Flex, Form, Input, Modal, Popconfirm, Spin, Typography, App, Card, Col,
    Row, Tooltip, Tree, MenuProps, Dropdown
} from "antd";
import { useEffect, useMemo, useState } from "react";
import {
    DeleteOutlined, SettingOutlined, EditOutlined, SaveOutlined, FilePdfOutlined, FileWordOutlined,
    ArrowLeftOutlined, FolderAddOutlined, FileAddOutlined, FolderOutlined, FileOutlined, MoreOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Project } from "../../stores/ProjectsStore";
import { ProjectDocument } from "../../stores/ProjectDocumentationStore";
import { useTranslation } from "react-i18next";
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { DataNode } from "antd/es/tree";
import { t } from "i18next";
import { Roles } from "../../enums/Roles";

const { Title } = Typography;

// Original DocumentEditor for backward compatibility with other files
export const DocumentEditor = ({
    initialContent,
    onChange
}: {
    initialContent?: string,
    onChange: (content: string) => void
}) => {
    const [editorState, setEditorState] = useState<EditorState | null>(null);

    useEffect(() => {
        // Initialize editor with content
        setTimeout(() => {
            let newEditorState;
            if (initialContent) {
                const contentBlock = htmlToDraft(initialContent);
                if (contentBlock) {
                    const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                    newEditorState = EditorState.createWithContent(contentState);
                } else {
                    newEditorState = EditorState.createEmpty();
                }
            } else {
                newEditorState = EditorState.createEmpty();
            }
            setEditorState(newEditorState);
        }, 100);
    }, [initialContent]);

    const handleEditorStateChange = (newState: EditorState) => {
        setEditorState(newState);
        const htmlContent = draftToHtml(convertToRaw(newState.getCurrentContent()));
        onChange(htmlContent);
    };

    if (!editorState) {
        return <div>{t("ProjectDocuments.LoadingEditor")}</div>;
    }

    return (
        <Editor
            editorState={editorState}
            onEditorStateChange={handleEditorStateChange}
            toolbar={{
                options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'history'],
                blockType: {
                    inDropdown: true,
                    options: ['Normal', 'H1', 'H2', 'H3'],
                },
                fontSize: {
                    options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
                },
            }}
            editorStyle={{
                height: '400px',
                maxHeight: '400px',
                overflowY: 'auto',
                border: '1px solid #d9d9d9',
                padding: '10px',
                borderRadius: '4px'
            }}
        />
    );
};

interface ProjectPageProps {
    isShowInternalLoader: boolean;
    getDocumentationByProjectProp?: () => Promise<ProjectDocument[]>;
    isAnonymous?: boolean;
}

const ProjectPage = observer(({
    isShowInternalLoader,
    getDocumentationByProjectProp,
    isAnonymous = false
}: ProjectPageProps) => {

    const [form] = Form.useForm();
    const { message } = App.useApp();
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Update param names to match new router
    const { projectSlug, documentId } = useParams<{ projectSlug?: string; documentId: string }>();

    const { applicationStore, projectsStore, projectDocumentationStore, projectDocumentationFolderStore, apiStore, anonymousStore } = useStore();

    const [project, setProject] = useState<Project | undefined>();
    const [projectId, setProjectId] = useState<string | undefined>();
    const [documents, setDocuments] = useState<ProjectDocument[]>([]);
    const [selectedDocument, setSelectedDocument] = useState<ProjectDocument | null>(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const location = useLocation();

    const [treeSelectedKeys, setTreeSelectedKeys] = useState<React.Key[]>([]);
    const [treeSelectedNode, setTreeSelectedNode] = useState<any | null>(null);

    const [isAddFolderOpen, setIsAddFolderOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState("");

    const [isRenameFolderOpen, setIsRenameFolderOpen] = useState(false);
    const [renameFolderName, setRenameFolderName] = useState("");
    const [renameFolderId, setRenameFolderId] = useState<string | null>(null);

    const [isRootBtnClicked, setIsRootBtnClicked] = useState(false);

    // Effect to load project by slug
    useEffect(() => {
        if (projectSlug) {
            loadProjectBySlug();
        }
    }, [projectSlug]);

    useEffect(() => {
        if (isAnonymous) {
            const params = new URLSearchParams(location.search);
            const token = params.get("token");

            if (!token) {
                return;
            }
            apiStore.setConfigApi({
                token,
                companyId: "0",
                userId: 0,
            });
            // loadProjectBySlug();
            loadProjectDocumentations();
        }
    }, [isAnonymous]);

    // Effect to load documents after project is loaded
    useEffect(() => {
        if (projectId) {
            loadProjectDocumentations();
        }
    }, [projectId]);

    // For reactive updating UI, when we update document
    useEffect(() => {
        if (!selectedDocument) return;
        const fresh = documents.find(d => String(d.id) === String(selectedDocument.id));
        if (fresh && fresh !== selectedDocument) {
            setSelectedDocument(fresh);
        }
    }, [documents]);



    const loadProjectBySlug = async () => {
        if (!projectSlug && !isAnonymous) return;
        try {
            setIsLoading(true);
            const loadedProject = await projectsStore.getBySlug(projectSlug!);
            if (loadedProject) {
                setProject(loadedProject);
                setProjectId(loadedProject.id.toString());
            } else {
                message.error(`${t("ProjectDocuments.ProjectNotFound")}: ${projectSlug}`);
                navigate(`/${applicationStore.company?.Tenant}/projects`);
            }
        } catch (error: any) {
            message.error(`${t("FailedLoadProject")}: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const loadProjectDocumentations = async () => {
        if (!projectId && !isAnonymous) return;
        try {
            setIsLoading(true);

            // documents
            let docs: ProjectDocument[] = [];
            if (getDocumentationByProjectProp) {
                docs = await getDocumentationByProjectProp();
            } else {
                docs = await projectDocumentationStore.getByProjectId(projectId!);
            }
            setDocuments(docs);

            // folders
            await projectDocumentationFolderStore.getByProjectId(projectId!);
        } catch (error: any) {
            message.error(`${t("ProjectDocuments.FailedLoadDocuments")}: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };


    /* --- Document operation --- */
    const handleDocumentSelect = (document: ProjectDocument) => {
        setSelectedDocument(document);
    };

    const showDocumentModal = (isNewDocument: boolean = false, preselectedFolderId?: number, forceRoot: boolean = false) => {
        if (!projectId) return;

        const folderId = forceRoot ? getRootFolderIdNum() : (preselectedFolderId ?? getTargetFolderId());

        if (isNewDocument) {
            form.resetFields();
            form.setFieldsValue({
                id: undefined,
                projectId,
                creatorId: applicationStore.user.id,
                folderId,
                title: "",
                content: { blocks: [] }
            });

            projectDocumentationStore.setDocument(null);
        }

        setIsRootBtnClicked(false);
        setIsModalOpen(true);
    };

    const hideDocumentModal = () => {
        setIsModalOpen(false);
        projectDocumentationStore.setDocument(null);
    };

    const openEditDocumentModal = (document: ProjectDocument) => {
        projectDocumentationStore.setDocument(document);

        form.setFieldsValue({
            id: document.id,
            title: document.title,
            content: document.content || '',
            projectId: document.projectId || projectId,
            creatorId: document.creatorId || applicationStore.user.id
        });

        showDocumentModal();
    };

    // Method wich we use when we click button in edit modal
    const handleDeleteDocumentFromModal = async () => {
        if (!projectDocumentationStore.document?.id) return;

        try {
            setIsLoading(true);
            message.loading({ content: t("ProjectDocuments.DeletingDocument"), key: 'deleteDocument' });

            await projectDocumentationStore.deleteById(projectDocumentationStore.document.id);

            message.success({ content: t("ProjectDocuments.DocumentDeletedSuccessfully"), key: 'deleteDocument' });

            // Reload documents and select the first one if available
            await loadProjectDocumentations();
            hideDocumentModal();

            if (documents.length > 0) {
                const firstDocument = documents[0];
                setSelectedDocument(firstDocument);
            }
        } catch (error: any) {
            message.error({ content: `${t("ProjectDocuments.FailedDeleteDocument")}: ${error.message}`, key: 'deleteDocument' });
        } finally {
            setIsLoading(false);
        }
    };

    // Method wich we use when we click button in folder tree
    const handleDeleteDocumentFromTree = async (docId: string | number) => {
        const ok = await projectDocumentationStore.deleteById(String(docId));
        if (ok) {
            message.success(t("ProjectDocuments.DocumentDeleted"));
            if (selectedDocument && String(selectedDocument.id) === String(docId)) {
                setSelectedDocument(null);
            }
            await loadProjectDocumentations();

            if (documents.length > 0) {
                const firstDocument = documents[0];
                setSelectedDocument(firstDocument);
            }
        } else {
            message.error(t("ProjectDocuments.FailedDeleteDocument"));
        }
    };
    /* -------------------------- */


    /* --- Folder documentation --- */
    const handleAddFolderClick = () => {
        setNewFolderName("");
        setIsAddFolderOpen(true);
    };

    const handleAddFolder = async () => {
        if (!projectId || !newFolderName.trim()) return;
        const parentId = getTargetFolderId();
        const payload = {
            projectId,
            name: newFolderName.trim(),
            parentFolderId: isRootBtnClicked ? getRootFolderIdStr() : String(parentId || getRootFolderIdStr()),
            isRoot: false,
            deleteStatus: false,
            orderIndex: (projectDocumentationFolderStore.documentsFolders?.length || 0) + 1
        };
        const created = await projectDocumentationFolderStore.createNew(payload);
        if (created) {
            message.success(t("ProjectDocuments.FolderCreated"));
            setIsAddFolderOpen(false);
            await loadProjectDocumentations();
        } else {
            message.error(t("ProjectDocuments.FailedCreateFolder"));
        }
        setIsRootBtnClicked(false);
    };

    const handleDeleteFolder = async (folderKey: string) => {
        const id = folderKey.split(":")[1];
        const ok = await projectDocumentationFolderStore.deleteById(id);
        if (ok) {
            message.success(t("ProjectDocuments.FolderDeleted"));
            if (treeSelectedNode && String(treeSelectedNode.key) === folderKey) {
                setTreeSelectedKeys([]);
                setTreeSelectedNode(null);
            }
            await loadProjectDocumentations();
        } else {
            message.error(t("ProjectDocuments.FailedDeleteFolder"));
        }
    };

    const getRootFolderIdStr = () => projectDocumentationFolderStore.rootFolderId;

    const getRootFolderIdNum = () => Number(getRootFolderIdStr());
    /* ---------------------------- */


    /* --- Actions for folder tree --- */
    // tree builder: folders + documents, with root folder hidden
    // tree builder: folders + documents, з урахуванням orderIndex
    const buildDirectoryTreeData = (
        folders: Array<{
            id?: string | number;
            name: string;
            parentFolderId?: string | number | null;
            isRoot?: boolean;
            orderIndex?: number | null;
        }>,
        docs: ProjectDocument[]
    ): DataNode[] => {
        const rootFolderIdStr = getRootFolderIdStr();

        // auxiliary
        const isRootFolder = (f: any) =>
            f?.isRoot === true ||
            String(f?.id) === String(rootFolderIdStr) ||
            f?.parentFolderId == null;

        // prepare a map of folders (to quickly get orderIndex)
        const folderById = new Map<string, any>();
        folders.forEach(f => folderById.set(String(f.id), f));

        // create folder nodes
        const folderMap: Record<string, (DataNode & { __id: string | number; __oi: number })> = {};
        folders.forEach(f => {
            const id = f.id as (string | number);
            folderMap[String(id)] = {
                key: `folder:${id}`,
                title: f.name,
                isLeaf: false,
                children: [],
                __id: id,
                __oi: f.orderIndex ?? Number.MAX_SAFE_INTEGER, // for sorting
            };
        });

        // we link folders (first we put the children in, then we sort them)
        const roots: (DataNode & { __oi?: number })[] = [];
        folders.forEach(f => {
            const id = String(f.id);
            const node = folderMap[id];
            const pid = f.parentFolderId != null ? String(f.parentFolderId) : null;

            if (pid && folderMap[pid] && !isRootFolder(f)) {
                (folderMap[pid].children as any[]).push(node);
            } else if (!isRootFolder(f)) {
                roots.push(node);
            }
        });

        // add documents to the appropriate folders / to the root (after the folders)
        docs.forEach((d, idx) => {
            const docNode: any = {
                key: String(d.id ?? `doc:${Math.random()}`),
                title: d.title || "Untitled",
                isLeaf: true,
                __oi: Number.MAX_SAFE_INTEGER - 1 + idx, // so that the documents are after the folders, but consistently
            };
            const fid = d.folderId != null ? Number(d.folderId) : null;
            const fidStr = fid != null ? String(fid) : null;
            const goesToRoot = fid == null || String(fid) === String(rootFolderIdStr) || !folderMap[fidStr ?? ""];

            if (goesToRoot) {
                roots.push(docNode);
            } else {
                (folderMap[fidStr!].children as any[]).push(docNode);
            }
        });

        // if there are hidden root folders, we show their children at the top level
        folders
            .filter(isRootFolder)
            .forEach(f => {
                const node = folderMap[String(f.id)];
                if (node?.children && (node.children as any[]).length) {
                    roots.push(...(node.children as any[]));
                }
            });

        // recursively sort the children of each folder by orderIndex
        const sortChildrenRecursive = (node: any) => {
            if (!node?.children) return;
            node.children = (node.children as any[]).sort((a, b) => {
                const ao = a.__oi ?? (folderById.get(String(a.__id))?.orderIndex ?? Number.MAX_SAFE_INTEGER);
                const bo = b.__oi ?? (folderById.get(String(b.__id))?.orderIndex ?? Number.MAX_SAFE_INTEGER);
                // Folders always before documents with the same indexes
                const aIsFolder = String(a.key).startsWith("folder:");
                const bIsFolder = String(b.key).startsWith("folder:");
                if (aIsFolder !== bIsFolder) return aIsFolder ? -1 : 1;
                return ao - bo;
            });
            node.children.forEach(sortChildrenRecursive);
        };

        // sort the top level and children
        const sortedRoots = roots.sort((a: any, b: any) => {
            const aIsFolder = String(a.key).startsWith("folder:");
            const bIsFolder = String(b.key).startsWith("folder:");
            if (aIsFolder !== bIsFolder) return aIsFolder ? -1 : 1;
            const ao = a.__oi ?? (a.__id ? (folderById.get(String(a.__id))?.orderIndex ?? Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER);
            const bo = b.__oi ?? (b.__id ? (folderById.get(String(b.__id))?.orderIndex ?? Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER);
            return ao - bo;
        });

        // let's go through each folder to sort its children
        Object.values(folderMap).forEach(sortChildrenRecursive);

        // we will clean the service fields before returning
        const stripMeta = (n: any): DataNode => ({
            key: n.key,
            title: n.title,
            isLeaf: n.isLeaf,
            children: n.children ? (n.children as any[]).map(stripMeta) : undefined,
        });

        return sortedRoots.map(stripMeta);
    };

    // memo for treeData to avoid re-listing
    const treeData: DataNode[] = useMemo(() => {
        return buildDirectoryTreeData(
            projectDocumentationFolderStore.documentsFolders || [],
            documents || []
        );
    }, [projectDocumentationFolderStore.documentsFolders, documents]);

    const getTargetFolderId = (): number => {
        const rootIdNum = getRootFolderIdNum();

        if (treeSelectedNode && String(treeSelectedNode.key).startsWith("folder:")) {
            const id = String(treeSelectedNode.key).split(":")[1];
            return Number(id || rootIdNum) || rootIdNum;
        }
        if (treeSelectedNode && treeSelectedNode.isLeaf) {
            const doc = documents.find(s => String(s.id) === String(treeSelectedNode.key));
            const fid = doc?.folderId != null ? Number(doc.folderId) : rootIdNum;
            return fid === 0 ? rootIdNum : fid;
        }
        return rootIdNum;
    };

    const isFolderKey = (key: React.Key) => String(key).startsWith("folder:");

    const isDocumentKey = (key: React.Key) => !isFolderKey(key);

    const isTopLevelNode = (node: any) => {
        const rootIdStr = getRootFolderIdStr();

        if (isFolderKey(node.key)) {
            const fId = getFolderIdFromKey(node.key);
            const f = getFolderById(fId);
            // top-level node = its parent === root
            return String(f?.parentFolderId ?? '') === String(rootIdStr);
        } else {
            const d = getDocByKey(node.key);
            // top-level document = folderId == root or null
            return !d?.folderId || String(d?.folderId) === String(rootIdStr);
        }
    };

    const getFolderIdFromKey = (key: React.Key) => String(key).split(":")[1];

    const getFolderById = (id?: string | number | null) =>
        projectDocumentationFolderStore.documentsFolders.find(f => String(f.id) === String(id));

    const getDocByKey = (key: React.Key) =>
        documents.find(d => String(d.id) === String(key));

    const isInSubtree = (rootFolderId: string, key: React.Key): boolean => {
        if (!isFolderKey(key)) return false;
        const targetId = getFolderIdFromKey(key);
        if (String(rootFolderId) === String(targetId)) return true;

        const visit = (pid: string): boolean => {
            const children = projectDocumentationFolderStore.documentsFolders
                .filter(f => String(f.parentFolderId ?? "") === String(pid));
            for (const ch of children) {
                if (String(ch.id) === String(targetId)) return true;
                if (visit(String(ch.id!))) return true;
            }
            return false;
        };
        return visit(rootFolderId);
    };

    const handleTreeDrop: Parameters<typeof Tree.DirectoryTree>[0]["onDrop"] = async (info) => {
        const { dragNode, node: dropNode, dropToGap, dropPosition } = info;

        // === CASE 1: FOLDERS ===
        if (isFolderKey(dragNode.key)) {
            if (!isFolderKey(dropNode.key)) {
                message.warning(t("ProjectDocuments.DropOnFoldersOnly") || "You can drop folders only onto folders");
                return;
            }

            const draggedFolderId = getFolderIdFromKey(dragNode.key);
            const draggedFolder = getFolderById(draggedFolderId);
            if (!draggedFolder) return;

            const dropFolderId = getFolderIdFromKey(dropNode.key);
            const dropFolder = getFolderById(dropFolderId);
            if (!dropFolder) return;

            if (isInSubtree(String(draggedFolderId), dropNode.key)) {
                message.warning(t("ProjectDocuments.CannotMoveIntoOwnSubtree") || "Cannot move a folder into its own subtree");
                return;
            }

            const rel = dropPosition as -1 | 0 | 1;

            const sortedChildren = (parentId: string | null) =>
                projectDocumentationFolderStore.documentsFolders
                    .filter(f => String(f.parentFolderId ?? null) === String(parentId))
                    .sort((a, b) => (a.orderIndex ?? Number.MAX_SAFE_INTEGER) - (b.orderIndex ?? Number.MAX_SAFE_INTEGER));

            let newParentId: string | null;
            let insertIndex: number;

            if (!dropToGap) {
                // drop inside the folder → first place
                newParentId = dropFolderId;
                insertIndex = 0;
            } else {
                // between folders → same parent as in dropFolder
                newParentId = dropFolder.parentFolderId ? String(dropFolder.parentFolderId) : null;

                const siblings = sortedChildren(newParentId)
                    .filter(f => String(f.id) !== String(draggedFolderId));

                const targetIdx = siblings.findIndex(f => String(f.id) === String(dropFolderId));
                const base = targetIdx === -1 ? siblings.length : targetIdx;

                insertIndex = rel === -1 ? base : base + 1;
            }

            const newOrderIndex = insertIndex + 1;

            await projectDocumentationFolderStore.updatePosition(
                String(draggedFolderId),
                newParentId,
                newOrderIndex
            );
            return;
        }

        // === CASE 2: DOCUMENTS ===
        if (isDocumentKey(dragNode.key)) {
            const doc = getDocByKey(dragNode.key);
            if (!doc || !doc.id) return;

            let targetFolderIdNum: number | null = null;

            // 1) If we drop BETWEEN top-level nodes (gap) → it is DROP TO ROOT
            if (dropToGap && isTopLevelNode(dropNode)) {
                targetFolderIdNum = getRootFolderIdNum();
            }
            // 2) Otherwise, if we drop it on a folder (inside or gap near the folder) → into this folder
            else if (isFolderKey(dropNode.key)) {
                targetFolderIdNum = Number(getFolderIdFromKey(dropNode.key));
            }
            // 3) Otherwise we do not allow
            else {
                message.warning(t("ProjectDocuments.DropDocumentsOnFoldersOnly") || "Drop documents onto folders only");
                return;
            }

            const prevDocs = documents.slice();
            const prevSelected = selectedDocument;

            const currentFolderIdNum = doc.folderId != null ? Number(doc.folderId) : getRootFolderIdNum();
            if (currentFolderIdNum === targetFolderIdNum) return;

            const targetFolderIdStr = String(targetFolderIdNum);

            // optimistically
            const nextDocs = prevDocs.map(d =>
                String(d.id) === String(doc.id) ? { ...d, folderId: targetFolderIdStr } : d
            );
            setDocuments(nextDocs);

            if (prevSelected && String(prevSelected.id) === String(doc.id)) {
                setSelectedDocument({ ...prevSelected, folderId: targetFolderIdStr });
            }

            try {
                await projectDocumentationStore.updatePosition({
                    id: String(doc.id),
                    folderId: targetFolderIdStr, // root = exactly rootId, NOT null
                } as any);
            } catch (e) {
                setDocuments(prevDocs);
                setSelectedDocument(prevSelected);
                message.error(t("ProjectDocuments.FailedMoveDocument") || "Failed to move document");
            }
            return;
        }
    };
    /* ------------------------------- */


    /* --- Form submission and export --- */
    // Update form submission to use projectId
    const handleFormSubmit = async (values: any) => {
        try {
            setIsLoading(true);
            message.loading({ content: t("ProjectDocuments.SavingDocument"), key: 'saveDocument' });

            // Convert content object to string if needed
            if (typeof values.content === 'object') {
                values.content = JSON.stringify(values.content);
            }

            if (!values.folderId) values.folderId = getTargetFolderId();

            let result: ProjectDocument | undefined;
            if (values.id) {
                // Edit existing document
                result = await projectDocumentationStore.edit(values);
            } else {
                // Create new document
                result = await projectDocumentationStore.createNew(values);
            }

            message.success({ content: t("ProjectDocuments.DocumentSavedSuccessfully"), key: 'saveDocument' });

            // Reload documents and select the new/edited one
            await loadProjectDocumentations();
            if (result && result.id) {
                setSelectedDocument(prev =>
                    prev && String(prev.id) === String(result!.id) ? { ...prev, ...result! } : prev
                );

                const updatedDocument = await projectDocumentationStore.getById(result.id);
                setSelectedDocument(updatedDocument);
            }

            hideDocumentModal();
        } catch (error: any) {
            message.error({ content: `${t("ProjectDocuments.FailedSaveDocument")}: ${error.message}`, key: 'saveDocument' });
        } finally {
            setIsLoading(false);
        }
    };
    /* ---------------------------------- */


    const handleExport = async (format: 'pdf' | 'docx') => {
        console.log("Exporting in format:", format);

        const pid =
            projectId ||
            (isAnonymous ? anonymousStore.accesses?.[0]?.projectId : undefined) ||
            documents?.[0]?.projectId;

        if (!pid) return;

        try {
            setIsExporting(true);

            isAnonymous ?
                await anonymousStore.exportDocumentation(pid, format) :
                await projectDocumentationStore.exportDocumentation(pid, format);

            message.success(`${t("ProjectDocuments.DocumentationExportedAs")} ${format.toUpperCase()}`);
        } catch (error: any) {
            message.error(`${t("ProjectDocuments.ExportFailed")}: ${error.message}`);
        } finally {
            setIsExporting(false);
        }
    };


    return (
        <div className="documentation-font-settings" style={{ maxWidth: "80%", margin: '20px auto' }}>
            {/* Header with navigation and buttons */}
            <Flex align="center" justify="space-between" style={{ marginBottom: 24 }}>
                <Flex align="center" gap={16}>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate(`/${applicationStore.company?.Tenant}/projects`)}
                    >
                        {t('Back')}
                    </Button>
                    <Title level={4} style={{ margin: 0 }}>
                        {project?.name || t('ProjectDescription')}
                    </Title>
                </Flex>

                <Flex gap={8}>
                    <Spin spinning={isExporting}>
                        <Button
                            type="primary"
                            icon={<FilePdfOutlined />}
                            onClick={() => handleExport('pdf')}
                        >
                            PDF
                        </Button>
                        <Button
                            type="primary"
                            icon={<FileWordOutlined />}
                            onClick={() => handleExport('docx')}
                            style={{ marginLeft: 8 }}
                        >
                            DOCX
                        </Button>
                    </Spin>

                    {!(!isAnonymous && applicationStore.user.roles?.some((role: any) => role.id === Roles.Viewer)) && (
                        <Button
                            type="primary"
                            icon={<SettingOutlined />}
                            disabled={
                                !(
                                    applicationStore.user?.roles?.some((role: any) =>
                                        [Roles.Owner, Roles.Manager].includes(role.id)
                                    ) ||
                                    project?.members?.some(
                                        (member: any) => member.id === applicationStore.user.id &&
                                            [Roles.Owner, Roles.Manager].includes((member as any).role.id)
                                    )
                                )
                            }
                            onClick={() => {
                                // Use slug for settings navigation
                                navigate(`/${applicationStore.company?.Tenant}/projects/${projectSlug}/settings`);
                            }}
                        />
                    )}
                </Flex>
            </Flex >

            <Card>
                <div style={{ padding: 20 }}>
                    <Spin spinning={isShowInternalLoader && (isLoading || projectsStore.isLoading || projectDocumentationStore.isLoading || project === undefined)}>

                        <Row gutter={16}>
                            <Col xs={24} md={9} lg={7} xl={6}>
                                <Card>
                                    {!isAnonymous &&
                                        !(applicationStore.user?.roles?.some((r: any) => r.id === Roles.Viewer)) && (
                                            <Flex gap={8} style={{ marginBottom: 16 }}>
                                                <Tooltip title={t("ProjectDocuments.AddFolder")}>
                                                    <Button
                                                        icon={<FolderAddOutlined />}
                                                        onClick={() => {
                                                            setIsRootBtnClicked(true);
                                                            handleAddFolderClick();
                                                        }}
                                                    />
                                                </Tooltip>
                                                <Tooltip title={t("ProjectDocuments.AddDocument")}>
                                                    <Button
                                                        icon={<FileAddOutlined />}
                                                        onClick={() => {
                                                            showDocumentModal(true, getTargetFolderId(), true);
                                                        }}
                                                    />
                                                </Tooltip>
                                            </Flex>
                                        )}

                                    <div
                                        style={{
                                            overflowX: 'auto',
                                            overflowY: 'hidden',
                                            paddingBottom: 4,
                                            width: '100%',
                                        }}
                                    >
                                        <Tree.DirectoryTree
                                            blockNode
                                            showIcon={false}
                                            defaultExpandAll
                                            draggable={{ icon: false }}
                                            allowDrop={({ dragNode, dropNode, dropPosition }) => {
                                                // ===== FOLDERS =====
                                                if (isFolderKey(dragNode.key)) {
                                                    // can only drop on/near folders (break)
                                                    if (!isFolderKey(dropNode.key)) return false;

                                                    // prohibit in own subtree
                                                    if (isInSubtree(getFolderIdFromKey(dragNode.key), dropNode.key)) return false;

                                                    // we do NOT block gap for empty folders: we allow both inside (0) and gap (-1/1)
                                                    return true;
                                                }

                                                // ===== DOCUMENTS =====
                                                if (isDocumentKey(dragNode.key)) {
                                                    // gap near the top-level node → allow (this will be root)
                                                    if (dropPosition !== 0 && isTopLevelNode(dropNode)) return true;

                                                    // throw on folder (inside or gap) → allow
                                                    if (isFolderKey(dropNode.key)) return true;

                                                    return false;
                                                }

                                                return false;
                                            }}
                                            onDrop={handleTreeDrop}
                                            treeData={treeData}
                                            selectedKeys={treeSelectedKeys}
                                            onSelect={(keys, info) => {
                                                setTreeSelectedKeys(keys as React.Key[]);
                                                setTreeSelectedNode(info.node);
                                                if (info.node.isLeaf) {
                                                    const doc = documents.find(s => String(s.id) === String(info.node.key));
                                                    if (doc) handleDocumentSelect(doc);
                                                }
                                            }}
                                            style={{ minWidth: 'max-content' }}
                                            titleRender={(node: any) => {
                                                const isFolder = String(node.key).startsWith('folder:');

                                                const onMenuClick: MenuProps['onClick'] = async ({ key, domEvent }) => {
                                                    domEvent.stopPropagation();

                                                    const isFolder = String(node.key).startsWith('folder:');
                                                    let targetFolderId: number;

                                                    if (isFolder) {
                                                        targetFolderId = Number(String(node.key).split(':')[1]) || getRootFolderIdNum();
                                                    } else {
                                                        const doc = documents.find(d => String(d.id) === String(node.key));
                                                        targetFolderId = (doc?.folderId != null ? Number(doc.folderId) : getRootFolderIdNum());
                                                    }

                                                    setTreeSelectedKeys([node.key]);
                                                    setTreeSelectedNode(node);

                                                    if (key === 'add-doc') return showDocumentModal(true, targetFolderId);
                                                    if (key === 'add-folder') return handleAddFolderClick();
                                                    if (key === 'rename-folder') {
                                                        if (!isFolder) return;
                                                        const idStr = String(node.key).split(':')[1];
                                                        setRenameFolderId(idStr);
                                                        setRenameFolderName(String(node.title) || "");
                                                        setIsRenameFolderOpen(true);
                                                        return;
                                                    }
                                                    if (key === 'delete-folder') {
                                                        return Modal.confirm({
                                                            title: t("ProjectDocuments.DeleteFolderQuestion"),
                                                            content: t("ProjectDocuments.DeleteFolderMessage"),
                                                            okButtonProps: { danger: true },
                                                            onOk: () => handleDeleteFolder(String(node.key)),
                                                        });
                                                    }
                                                    if (key === 'delete-doc') {
                                                        return Modal.confirm({
                                                            title: t("ProjectDocuments.DeleteDocumentQuestion"),
                                                            okButtonProps: { danger: true },
                                                            onOk: () => handleDeleteDocumentFromTree(node.key),
                                                        });
                                                    }
                                                };

                                                const items: MenuProps['items'] = isFolder
                                                    ? [
                                                        { key: 'add-doc', label: t('ProjectDocuments.AddDocument'), icon: <FileAddOutlined /> },
                                                        { key: 'add-folder', label: t('ProjectDocuments.AddFolder'), icon: <FolderAddOutlined /> },
                                                        { key: 'rename-folder', label: t('ProjectDocuments.Rename'), icon: <EditOutlined /> },
                                                        { type: 'divider' as const },
                                                        { key: 'delete-folder', label: t('ProjectDocuments.DeleteFolder'), icon: <DeleteOutlined />, danger: true },
                                                    ]
                                                    : [{ key: 'delete-doc', label: t('ProjectDocuments.DeleteDocument'), icon: <DeleteOutlined />, danger: true }];

                                                return (
                                                    <div
                                                    className="documentation-files"
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 8,
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {isFolder ? <FolderOutlined /> : <FileOutlined />}
                                                        <span
                                                            style={{
                                                                whiteSpace: 'nowrap',
                                                                // If we need hide long names:
                                                                // overflow: 'hidden',
                                                                // textOverflow: 'ellipsis',
                                                                // maxWidth: 360,
                                                            }}
                                                        >
                                                            {node.title}
                                                        </span>
                                                        <Dropdown menu={{ items, onClick: onMenuClick }} trigger={['click']}>
                                                            <Button
                                                                size="small"
                                                                type="text"
                                                                icon={<MoreOutlined />}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        </Dropdown>
                                                    </div>
                                                );
                                            }}
                                        />
                                    </div>

                                    {documents.length === 0 && (
                                        <div style={{ padding: 20, textAlign: "center", color: "#999" }}>
                                            {t("ProjectDocuments.NoDocumentsAvailable")}
                                        </div>
                                    )}
                                </Card>
                            </Col>

                            <Col xs={24} md={15} lg={17} xl={18} style={{ minWidth: 0 }}>
                                <Card style={{ minWidth: 0 }}>
                                    {selectedDocument ? (
                                        <Card
                                            bordered={false}
                                            style={{ position: 'relative', minWidth: 0 }}
                                            title={selectedDocument.title}
                                            extra={
                                                !isAnonymous && !applicationStore.user.roles?.some((role: any) => role.id === Roles.Viewer) && (
                                                    <Button
                                                        type="primary"
                                                        icon={<EditOutlined />}
                                                        onClick={() => openEditDocumentModal(selectedDocument)}
                                                    >
                                                        {t('Edit')}
                                                    </Button>
                                                )
                                            }
                                        >
                                            <div className="document-content documentation-text" style={{ minWidth: 0 }}>
                                                <div
                                                    className="html-content"
                                                    style={{ overflowX: 'auto' }}
                                                    dangerouslySetInnerHTML={{ __html: selectedDocument.content || '' }}
                                                />
                                            </div>
                                        </Card>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '100px 0', color: '#999' }}>
                                            {documents.length > 0
                                                ? t("ProjectDocuments.SelectDocumentViewContent")
                                                : t("ProjectDocuments.NoContentAvailable")}
                                        </div>
                                    )}
                                </Card>
                            </Col>
                        </Row>

                    </Spin>
                </div>
            </Card>

            {/* Edit/Create Document Modal */}
            {!isAnonymous && (
                <>
                    <Modal
                        title={projectDocumentationStore.document ? `${t("ProjectDocuments.EditDocument")}: ${projectDocumentationStore.document.title}` : t("CreateDocument")}
                        open={isModalOpen}
                        footer={null}
                        onCancel={hideDocumentModal}
                        width={900}
                        destroyOnClose
                    >
                        <Form
                            form={form}
                            onFinish={handleFormSubmit}
                            layout="vertical"
                            initialValues={{
                                projectId: projectId,
                                creatorId: applicationStore.user.id,
                                content: ''
                            }}
                        >
                            <Form.Item name="id" hidden>
                                <Input />
                            </Form.Item>
                            <Form.Item name="projectId" hidden>
                                <Input />
                            </Form.Item>
                            <Form.Item name="creatorId" hidden>
                                <Input />
                            </Form.Item>
                            <Form.Item name="folderId" hidden>
                                <Input />
                            </Form.Item>

                            <Form.Item
                                label={t("Title")}
                                name="title"
                                rules={[{ required: true, message: t("ProjectDocuments.EnterTitleMessage") }]}
                            >
                                <Input
                                    placeholder={t("ProjectDocuments.EnterTitle")}
                                    disabled={projectDocumentationStore.document?.isDefault}
                                />
                            </Form.Item>

                            <Form.Item
                                label={t("ProjectDocuments.Content")}
                                name="content"
                                rules={[{ required: true, message: t("ProjectDocuments.EnterContentMessage") }]}
                            >
                                <DocumentEditor
                                    initialContent={projectDocumentationStore.document?.content}
                                    onChange={(content) => form.setFieldValue('content', content)}
                                />
                            </Form.Item>

                            <Flex justify="space-between" style={{ marginTop: 24 }}>
                                {projectDocumentationStore.document && (
                                    <Popconfirm
                                        title={t("ProjectDocuments.DeleteDocumentQuestion")}
                                        description={t("ThisActionCannotBeUndone")}
                                        okText={t("Delete")}
                                        cancelText={t("Cancel")}
                                        okButtonProps={{ danger: true }}
                                        onConfirm={handleDeleteDocumentFromModal}
                                    >
                                        <Button
                                            danger
                                            icon={<DeleteOutlined />}
                                            disabled={projectDocumentationStore.document?.isDefault}
                                        >
                                            {t("ProjectDocuments.DeleteDocument")}
                                        </Button>
                                    </Popconfirm>
                                )}

                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={<SaveOutlined />}
                                    loading={isLoading}
                                >
                                    {projectDocumentationStore.document ? t("ProjectDocuments.SaveChanges") : t("ProjectDocuments.CreateDocument")}
                                </Button>
                            </Flex>
                        </Form>
                    </Modal>

                    <Modal
                        open={isAddFolderOpen}
                        title={t("ProjectDocuments.CreateFolder")}
                        onCancel={() => !projectDocumentationFolderStore.isLoading && setIsAddFolderOpen(false)}
                        onOk={handleAddFolder}
                        okText={t("Create")}
                        confirmLoading={projectDocumentationFolderStore.isLoading}
                        cancelButtonProps={{ disabled: projectDocumentationFolderStore.isLoading }}
                        destroyOnClose
                    >
                        <Spin spinning={projectDocumentationFolderStore.isLoading}>
                            <Input
                                autoFocus
                                placeholder={t("ProjectDocuments.FolderName")}
                                value={newFolderName}
                                onChange={e => setNewFolderName(e.target.value)}
                                disabled={projectDocumentationFolderStore.isLoading}
                            />
                        </Spin>
                    </Modal>

                    <Modal
                        open={isRenameFolderOpen}
                        title={t("ProjectDocuments.RenameFolder")}
                        onCancel={() => !projectDocumentationFolderStore.isLoading && setIsRenameFolderOpen(false)}
                        onOk={async () => {
                            const newName = renameFolderName.trim();
                            if (!renameFolderId) return;
                            if (!newName) {
                                message.warning(t("ProjectDocuments.NameCannotBeEmpty"));
                                return;
                            }
                            try {
                                await projectDocumentationFolderStore.updateName(renameFolderId, newName);
                                message.success(t("ProjectDocuments.FolderRenamed"));
                                setIsRenameFolderOpen(false);
                                //await projectDocumentationFolderStore.getByProjectId(projectId!);
                            } catch {
                                message.error(t("ProjectDocuments.FailedRenameFolder"));
                            }
                        }}
                        okText={t("Save")}
                        confirmLoading={projectDocumentationFolderStore.isLoading}
                        cancelButtonProps={{ disabled: projectDocumentationFolderStore.isLoading }}
                        destroyOnClose
                    >
                        <Spin spinning={projectDocumentationFolderStore.isLoading}>
                            <Input
                                autoFocus
                                placeholder={t("ProjectDocuments.FolderName")}
                                value={renameFolderName}
                                onChange={e => setRenameFolderName(e.target.value)}
                                onPressEnter={(e) => {
                                    e.preventDefault();
                                    const newName = renameFolderName.trim();
                                    if (!renameFolderId) return;
                                    if (!newName) {
                                        message.warning(t("ProjectDocuments.NameCannotBeEmpty"));
                                        return;
                                    }
                                    (async () => {
                                        try {
                                            await projectDocumentationFolderStore.updateName(renameFolderId, newName);
                                            message.success(t("ProjectDocuments.FolderRenamed"));
                                            setIsRenameFolderOpen(false);
                                        } catch {
                                            message.error(t("ProjectDocuments.FailedRenameFolder"));
                                        }
                                    })();
                                }}
                                disabled={projectDocumentationFolderStore.isLoading}
                            />
                        </Spin>
                    </Modal>
                </>
            )}
        </div>
    );
});

export default ProjectPage;